'use client'
import { motion, type Transition } from 'motion/react'
import type { PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'
import { LogoHomeLink } from '@/components/LogoHomeLink'
import { SKIP_TARGET_ID } from '@/components/SkipToMainContentTarget/SkipToMainContentTarget'
import { isBrowser } from '@/utils/env'
import { useScrollState } from '@/utils/useScrollState'
import { Button } from 'ui/src/components/Button/Button'
import { framerTransitions } from 'ui/src/theme/transitions'
import { bodyBgColor } from 'ui/src/theme/vars.css'
import {
  contentWrapper,
  ghostWrapper,
  logoWrapper,
  MENU_BAR_HEIGHT_PX,
  menuWrapper,
  skipToMainContent,
  wrapper,
} from './Header.css'

const HEADER_TRANSITION: Transition = {
  ...framerTransitions.easeInOutCubic,
}

const ANIMATION_VARIANTS = {
  SLIDE_IN: {
    position: 'fixed',
    y: 0,
    backgroundColor: bodyBgColor,
    boxShadow: 'rgba(0, 0, 0, 0.06) 0px 2px 12px',
  },
  SLIDE_OUT: {
    position: 'fixed',
    y: '-150%',
  },
  HIDE: {
    y: '-150%',
    transition: { duration: 0 },
  },
} as const

type AnimationVariant = keyof typeof ANIMATION_VARIANTS | undefined

export const Header = (props: PropsWithChildren<unknown>) => {
  const { children } = props
  const { t } = useTranslation('common')
  const scrollState = useScrollState({ threshold: MENU_BAR_HEIGHT_PX * 2 })

  let isOverlayMenu = false
  let animate: AnimationVariant = undefined
  if (isBrowser()) {
    const main = window.document.querySelector('main[data-header-mode]')
    const headerMode = main?.getAttribute('data-header-mode')
    isOverlayMenu = main?.getAttribute('data-overlay-menu') === 'true'

    if (headerMode === 'sticky') {
      switch (scrollState) {
        case 'SCROLL_UP':
          animate = 'SLIDE_IN'
          break
        case 'SCROLL_DOWN':
          animate = 'SLIDE_OUT'
          break
        case 'BELOW':
          animate = 'HIDE'
          break
      }
    }
  }

  // If the page has overlayMenu set to true in Storyblok, the header should be transparent
  const initialStyles = {
    backgroundColor: isOverlayMenu ? 'transparent' : bodyBgColor,
  }

  return (
    <div className={ghostWrapper}>
      <Button as="a" href={`#${SKIP_TARGET_ID}`} size="medium" className={skipToMainContent}>
        {t('SKIP_TO_MAIN_CONTENT')}
      </Button>
      <motion.header
        className={wrapper}
        initial={initialStyles}
        variants={ANIMATION_VARIANTS}
        animate={animate}
        transition={HEADER_TRANSITION}
      >
        <div className={contentWrapper}>
          {children ? <div className={menuWrapper}>{children}</div> : null}
          <div className={logoWrapper}>
            <LogoHomeLink />
          </div>
        </div>
      </motion.header>
    </div>
  )
}
